import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, User, Lock, Building2, CheckCircle, Store, Upload, ImageIcon, Gift, Copy, Check, Users, TrendingUp, CreditCard, ShieldCheck, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { BUSINESS_TYPES, BUSINESS_TYPE_LIST, type BusinessType } from "@/lib/business-types";
import { useTheme, type ThemePreference } from "@/context/theme";
import { Monitor, Moon, Sun } from "lucide-react";
import { Link } from "wouter";
import { MerchantDataRequests } from "@/components/merchant-data-requests";
import { EmailPreferencesCard } from "@/components/email-preferences-card";
import { EmailChangeCard } from "@/components/email-change-card";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const ROLE_LABELS: Record<string, string> = {
  superadmin: "Super Admin",
  admin: "Owner",
  manager: "Manager",
  staff: "Staff",
};

function ReferralCard({ referralCode, storeName }: { referralCode: string | null; storeName: string }) {
  const [copied, setCopied] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const code = referralCode ?? "—";
  const signupUrl = `https://retailpulsepos.com/signup?ref=${code}`;
  const shareText = `Join RetailPulse — providing intelligent retail solutions for Nigerian retailers. Sign up free using my code ${code}: ${signupUrl}`;

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["referral-stats"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/referrals/stats`, { credentials: "include" });
      return res.ok ? res.json() : null;
    },
  });

  const handleCopy = (text: string, which: "code" | "msg") => {
    navigator.clipboard.writeText(text);
    if (which === "code") { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    else { setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 2000); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gift className="w-4 h-4 text-primary" /> Referral Programme
        </CardTitle>
        <CardDescription>
          Share your code with other retailers. You earn <strong>+10 days free trial</strong> for every signup, plus <strong>5% commission</strong> on every plan they subscribe to — forever.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-muted/50 rounded-xl border border-border p-3 text-center">
            <Users className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
            <p className="text-xl font-bold">{statsLoading ? "—" : (stats?.signupsCount ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Retailers referred</p>
          </div>
          <div className="bg-muted/50 rounded-xl border border-border p-3 text-center">
            <TrendingUp className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
            <p className="text-xl font-bold">{statsLoading ? "—" : (stats?.commissionsCount ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Subscriptions</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800/40 p-3 text-center">
            <Gift className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
              {statsLoading ? "—" : formatCurrency(stats?.totalCommission ?? 0)}
            </p>
            <p className="text-xs text-muted-foreground">Total earned</p>
          </div>
        </div>

        {/* Referral code */}
        <div className="bg-muted/50 rounded-xl border border-border p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Your referral code</p>
            <p className="text-2xl font-mono font-bold tracking-widest">{code}</p>
          </div>
          <button
            onClick={() => handleCopy(code, "code")}
            className="flex items-center gap-1.5 text-sm text-primary hover:underline font-medium"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy code"}
          </button>
        </div>

        {/* Share message */}
        <div className="flex items-center gap-3">
          <div className="flex-1 text-sm border border-border rounded-lg px-4 py-2.5 text-muted-foreground bg-muted/30 truncate">
            {shareText}
          </div>
          <button
            onClick={() => handleCopy(shareText, "msg")}
            className="flex-shrink-0 flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
          >
            {copiedMsg ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedMsg ? "Copied!" : "Copy message"}
          </button>
        </div>

        {/* Recent commissions */}
        {stats?.commissions?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recent commissions</p>
            <div className="space-y-1.5">
              {stats.commissions.slice(0, 5).map((c: any) => (
                <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                  <div>
                    <span className="capitalize font-medium">{c.plan} plan</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {new Date(c.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <span className="font-semibold text-emerald-600">{formatCurrency(c.commissionAmount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          There's no limit — refer as many stores as you like. Commissions are tracked automatically every time a referred retailer renews or upgrades their plan.
        </p>
      </CardContent>
    </Card>
  );
}

type PaystackBank = { name: string; slug: string; code: string; id: number };

function PaymentSettlementCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [bankCode, setBankCode] = useState("");
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [accountNumber, setAccountNumber] = useState("");
  const [verifiedName, setVerifiedName] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

  const { data: banks = [], isLoading: banksLoading } = useQuery<PaystackBank[]>({
    queryKey: ["paystack-banks"],
    queryFn: async () => {
      const r = await fetch(`${BASE_URL}/api/auth/paystack-banks`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 1000 * 60 * 60, // cache for 1 hour
  });

  const { data: subAccount, isLoading: subAccountLoading } = useQuery<{
    registered: boolean; subAccountCode: string | null; subAccountId: string | null;
  }>({
    queryKey: ["paystack-subaccount"],
    queryFn: async () => {
      const r = await fetch(`${BASE_URL}/api/auth/paystack-subaccount`, { credentials: "include" });
      if (!r.ok) return { registered: false, subAccountCode: null, subAccountId: null };
      return r.json();
    },
  });

  const handleVerify = async () => {
    if (!accountNumber || accountNumber.length !== 10 || !bankCode) {
      toast({ title: "Enter a 10-digit account number and select a bank", variant: "destructive" });
      return;
    }
    setVerifying(true);
    setVerifiedName(null);
    try {
      const r = await fetch(`${BASE_URL}/api/auth/paystack-resolve-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accountNumber, bankCode }),
      });
      const data = await r.json();
      if (!r.ok) { toast({ title: data.error ?? "Could not verify account", variant: "destructive" }); return; }
      setVerifiedName(data.accountName);
    } finally {
      setVerifying(false);
    }
  };

  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!verifiedName) throw new Error("Please verify the account first");
      const r = await fetch(`${BASE_URL}/api/auth/paystack-subaccount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ businessName: verifiedName, settlementBank: bankCode, accountNumber }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? "Registration failed");
      return data;
    },
    onSuccess: (data) => {
      toast({ title: data.updated ? "Settlement account updated" : "Settlement account registered!", description: "Virtual terminal payments will now settle directly to your bank." });
      qc.invalidateQueries({ queryKey: ["paystack-subaccount"] });
      setVerifiedName(null);
      setAccountNumber("");
      setBankCode("");
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const selectedBank = banks.find(b => b.code === bankCode);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="w-4 h-4 text-primary" /> Payment Settlement
        </CardTitle>
        <CardDescription>
          Register your bank account to receive Virtual Terminal payments directly. Customers pay via bank transfer and funds settle to your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {subAccountLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : subAccount?.registered ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <div>
              <p className="font-medium">Settlement account registered</p>
              <p className="text-xs text-green-700 mt-0.5">Code: {subAccount.subAccountCode}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <CreditCard className="w-4 h-4 shrink-0" />
            <p>No settlement account yet. Virtual terminal payments go to the platform account until you register below.</p>
          </div>
        )}

        <Separator />
        <p className="text-sm font-medium">{subAccount?.registered ? "Update settlement bank" : "Register settlement bank"}</p>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Bank</Label>
            <Popover open={bankPickerOpen} onOpenChange={setBankPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={bankPickerOpen}
                  disabled={banksLoading}
                  className="w-full justify-between text-sm font-normal h-9"
                >
                  <span className={cn("truncate", !bankCode && "text-muted-foreground")}>
                    {banksLoading
                      ? "Loading banks…"
                      : bankCode
                        ? banks.find(b => b.code === bankCode)?.name
                        : "Select your bank"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search bank…" className="text-sm" />
                  <CommandList>
                    <CommandEmpty>No bank found.</CommandEmpty>
                    <CommandGroup>
                      {banks.map(b => (
                        <CommandItem
                          key={b.code}
                          value={b.name}
                          onSelect={() => {
                            setBankCode(b.code);
                            setVerifiedName(null);
                            setBankPickerOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", bankCode === b.code ? "opacity-100" : "opacity-0")} />
                          {b.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Account Number</Label>
            <div className="flex gap-2">
              <Input
                className="text-sm"
                placeholder="0000000000"
                maxLength={10}
                value={accountNumber}
                onChange={e => { setAccountNumber(e.target.value.replace(/\D/g, "")); setVerifiedName(null); }}
              />
              <Button type="button" variant="outline" size="sm" onClick={handleVerify} disabled={verifying || !bankCode || accountNumber.length !== 10}>
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
              </Button>
            </div>
          </div>

          {verifiedName && (
            <div className="flex items-center gap-2 p-2.5 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <div>
                <p className="font-medium">{verifiedName}</p>
                <p className="text-xs text-green-600">{selectedBank?.name} · {accountNumber}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-1">
          <Button
            onClick={() => registerMutation.mutate()}
            disabled={!verifiedName || registerMutation.isPending}
            size="sm"
            className="gap-2"
          >
            {registerMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {subAccount?.registered ? "Update Bank Account" : "Register Bank Account"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user, store, refetch } = useAuth();
  const { toast } = useToast();
  const { theme, resolvedTheme, setTheme } = useTheme();

  const [profileName, setProfileName] = useState(user?.name ?? "");
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [btLoading, setBtLoading] = useState<string | null>(null);

  // Store branding state
  const [storeName, setStoreName] = useState(store?.name ?? "");
  const [storeAddress, setStoreAddress] = useState(store?.address ?? "");
  const [storePhone, setStorePhone] = useState(store?.phone ?? "");
  const [receiptFooter, setReceiptFooter] = useState(store?.receiptFooter ?? "");
  const [receiptPrefix, setReceiptPrefix] = useState(store?.receiptPrefix ?? "");
  const [barcodePrefix, setBarcodePrefix] = useState(store?.barcodePrefix ?? "");
  const [loyaltyEarnRate, setLoyaltyEarnRate] = useState(String(store?.loyaltyEarnRate ?? 1));
  const [vatRate, setVatRate] = useState(String(store?.taxRate ?? 7.5));
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (store) setVatRate(String(store.taxRate ?? 7.5));
  }, [store?.id, store?.taxRate]);

  const handleSaveName = async () => {
    if (!profileName.trim()) return;
    setProfileLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: profileName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { toast({ title: "Error", description: data.error, variant: "destructive" }); return; }
      setVatRate(String(data.taxRate ?? 7.5));
      await refetch();
      toast({ title: "Profile updated", description: "Your name has been saved." });
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: "Error", description: "All password fields are required.", variant: "destructive" }); return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords don't match.", variant: "destructive" }); return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters.", variant: "destructive" }); return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { toast({ title: "Error", description: data.error, variant: "destructive" }); return; }
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast({ title: "Password changed", description: "Your password has been updated." });
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Error", description: "Please select an image file.", variant: "destructive" }); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Error", description: "Image must be under 2MB.", variant: "destructive" }); return;
    }
    setLogoUploading(true);
    try {
      const urlRes = await fetch(`${BASE}/api/storage/uploads/request-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      const { uploadURL, objectPath } = await urlRes.json();
      await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      const saveRes = await fetch(`${BASE}/api/auth/store`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ logoUrl: objectPath }),
      });
      if (!saveRes.ok) throw new Error("Failed to save logo");
      setLogoPreview(URL.createObjectURL(file));
      await refetch();
      toast({ title: "Logo updated", description: "Your store logo has been saved." });
    } catch {
      toast({ title: "Error", description: "Failed to upload logo. Please try again.", variant: "destructive" });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveBranding = async () => {
    const rateText = vatRate.trim();
    const parsedVatRate = Number(rateText);
    if (!/^\d+(?:\.\d{1,2})?$/.test(rateText) || !Number.isFinite(parsedVatRate) || parsedVatRate < 0 || parsedVatRate > 100) {
      toast({ title: "Invalid VAT rate", description: "Enter a rate from 0 to 100 with up to 2 decimal places.", variant: "destructive" });
      return;
    }
    setBrandingLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/store`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: storeName, address: storeAddress, phone: storePhone, receiptFooter, receiptPrefix, barcodePrefix, loyaltyEarnRate: Number(loyaltyEarnRate), taxRate: parsedVatRate }),
      });
      const data = await res.json();
      if (!res.ok) { toast({ title: "Error", description: data.error, variant: "destructive" }); return; }
      await refetch();
      toast({ title: "Store details saved", description: "Your store info will now appear on receipts." });
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleBusinessType = async (type: string) => {
    if (type === user?.businessType) return;
    setBtLoading(type);
    try {
      const res = await fetch(`${BASE}/api/auth/business-type`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ businessType: type }),
      });
      const data = await res.json();
      if (!res.ok) { toast({ title: "Error", description: data.error, variant: "destructive" }); return; }
      await refetch();
      const config = BUSINESS_TYPES[type as BusinessType];
      toast({ title: "Business type updated", description: `Switched to ${config.emoji} ${config.label}. Your dashboard has been updated.` });
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setBtLoading(null);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your profile and account preferences</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="w-4 h-4" /> Profile
          </CardTitle>
          <CardDescription>Update your display name</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="Your name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email ?? ""} disabled className="bg-muted" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="space-y-1.5 flex-1">
              <Label>Role</Label>
              <div className="flex items-center h-10">
                <Badge variant="secondary">{ROLE_LABELS[user?.role ?? ""] ?? user?.role}</Badge>
              </div>
            </div>
            <div className="space-y-1.5 flex-1">
              <Label>Store</Label>
              <div className="flex items-center h-10">
                <span className="text-sm">{store?.name}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSaveName} disabled={profileLoading || profileName === user?.name}>
              {profileLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Name
            </Button>
          </div>
        </CardContent>
      </Card>

      {user?.role === "admin" || user?.role === "superadmin" ? <EmailChangeCard /> : null}

      <EmailPreferencesCard />

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {resolvedTheme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            Appearance
          </CardTitle>
          <CardDescription>Choose how RetailPulse looks on this device</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {([
              { value: "light", label: "Light", description: "Always use light mode", icon: Sun },
              { value: "dark", label: "Dark", description: "Always use dark mode", icon: Moon },
              { value: "system", label: "System", description: "Match this device", icon: Monitor },
            ] as const).map(option => {
              const Icon = option.icon;
              const selected = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTheme(option.value as ThemePreference)}
                  aria-pressed={selected}
                  className={cn(
                    "rounded-xl border-2 p-4 text-left transition-colors",
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40 hover:bg-muted/40",
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <Icon className={cn("w-5 h-5", selected ? "text-primary" : "text-muted-foreground")} />
                    {selected && <CheckCircle className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="font-semibold mt-3">{option.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="w-4 h-4" /> Change Password
          </CardTitle>
          <CardDescription>Update your login password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Current Password</Label>
            <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleChangePassword} disabled={passwordLoading} variant="outline">
              {passwordLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Business Type */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4" /> Business Type
          </CardTitle>
          <CardDescription>
            Your business type controls the dashboard layout and which features are shown in the sidebar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            {BUSINESS_TYPE_LIST.map(({ type, emoji, label, tagline }) => {
              const isActive = user?.businessType === type;
              return (
                <button
                  key={type}
                  onClick={() => handleBusinessType(type)}
                  disabled={!!btLoading}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border text-left transition-all ${
                    isActive
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{emoji}</span>
                    <div>
                      <p className="font-medium text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground">{tagline}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    {btLoading === type && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    {isActive && !btLoading && <CheckCircle className="w-5 h-5 text-primary" />}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Store Branding */}
      {(user?.role === "admin" || user?.role === "superadmin") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="w-4 h-4" /> Store Details & Branding
            </CardTitle>
            <CardDescription>
              Your logo, address and contact info will appear on printed receipts and invoices
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Logo */}
            <div className="space-y-2">
              <Label>Store Logo</Label>
              <div className="flex items-center gap-4">
                <div
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/40 cursor-pointer hover:border-primary/50 transition-colors overflow-hidden flex-shrink-0"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoPreview || store?.logoUrl ? (
                    <img
                      src={logoPreview ?? `${BASE}/api/storage${store?.logoUrl}`}
                      alt="Store logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {logoUploading ? "Uploading…" : "Upload Logo"}
                  </Button>
                  <p className="text-xs text-muted-foreground">PNG, JPG or SVG · max 2 MB</p>
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = ""; }}
                />
              </div>
            </div>

            <Separator />

            {/* Store name + phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Store Name</Label>
                <Input value={storeName} onChange={e => setStoreName(e.target.value)} placeholder="Your store name" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input value={storePhone} onChange={e => setStorePhone(e.target.value)} placeholder="+234 800 000 0000" />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-1.5">
              <Label>Store Address</Label>
              <Textarea
                value={storeAddress}
                onChange={e => setStoreAddress(e.target.value)}
                placeholder="12 Market Street, Ikeja, Lagos"
                rows={2}
                className="resize-none"
              />
            </div>

            {/* Receipt number prefix */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Receipt Number Prefix</Label>
                <Input
                  value={receiptPrefix}
                  onChange={e => setReceiptPrefix(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6))}
                  placeholder="e.g. GLD, ABK, MRT"
                  maxLength={6}
                />
                <p className="text-xs text-muted-foreground">
                  2–6 letters. Receipts will look like <span className="font-mono font-medium">{receiptPrefix || "GLD"}001-000001</span>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Receipt Footer Message</Label>
                <Input
                  value={receiptFooter}
                  onChange={e => setReceiptFooter(e.target.value)}
                  placeholder="Thank you for shopping with us!"
                />
                <p className="text-xs text-muted-foreground">Shown at the bottom of every printed receipt</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Product Barcode Prefix</Label>
              <Input
                value={barcodePrefix}
                onChange={e => setBarcodePrefix(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6))}
                placeholder="e.g. PIC"
                minLength={2}
                maxLength={6}
                className="max-w-xs font-mono"
              />
              <p className="text-xs text-muted-foreground">
                2–6 letters or numbers. Generated product barcodes will look like{" "}
                <span className="font-mono font-medium">{barcodePrefix || storeName.replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase() || "SKU"}00000001</span>.
                Existing product barcodes will not change.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Loyalty Points Earn Rate</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={loyaltyEarnRate}
                  onChange={e => setLoyaltyEarnRate(e.target.value)}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">point{Number(loyaltyEarnRate) !== 1 ? "s" : ""} per ₦100 spent</span>
              </div>
              <p className="text-xs text-muted-foreground">
                e.g. set to <span className="font-medium">1</span> → customer spending ₦5,000 earns <span className="font-medium">50 points</span>. Set to <span className="font-medium">0</span> to disable.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="store-vat-rate">VAT Rate (%)</Label>
              <Input
                id="store-vat-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={vatRate}
                onChange={e => setVatRate(e.target.value)}
                className="w-28"
              />
              <p className="text-xs text-muted-foreground">
                Set to 0 to use the 7.5% Nigeria default. This rate applies only to products marked VAT-applicable; confirm product classifications against current tax guidance.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveBranding} disabled={brandingLoading} className="gap-2">
                {brandingLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Store Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Settlement — admin only */}
      {(user?.role === "admin" || user?.role === "superadmin") && (
        <PaymentSettlementCard />
      )}

      {/* Referral Programme */}
      {(user?.role === "admin" || user?.role === "superadmin") && (
        <ReferralCard referralCode={store?.referralCode ?? null} storeName={store?.name ?? ""} />
      )}

      {/* Store info */}
      {user?.role === "admin" && <MerchantDataRequests />}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Plan</span>
            <Badge variant="outline" className="capitalize">{store?.plan}</Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Status</span>
            <Badge variant={store?.subscriptionStatus === "active" ? "default" : "secondary"} className="capitalize">
              {store?.subscriptionStatus}
            </Badge>
          </div>
          {store?.subscriptionStatus === "trial" && (
            <>
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Trial ends</span>
                <span>{new Date(store.trialEndsAt).toLocaleDateString("en-NG", { year: "numeric", month: "long", day: "numeric" })}</span>
              </div>
            </>
          )}
          {(user?.role === "admin" || user?.role === "superadmin") && (
            <>
              <Separator />
              <Button asChild className="w-full sm:w-auto min-h-11">
                <Link href="/subscribe">Manage Subscription</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
